/**
 * Dispatch board state.
 *
 * Orders are real Spire data (fetched by the route via TanStack Query).
 * Assignments — "this order is assigned to tech X on date D starting at hour
 * Y" — live on the Spire order itself in four FOP-prefixed UDF keys:
 *
 *   FOP_TECH      → technician id (matches a TECHS row)
 *   FOP_DATE      → YYYY-MM-DD (local date) the assignment is scheduled for
 *   FOP_START     → clock time "HH:MM" (legacy: integer slot offset from 8 AM)
 *   FOP_DURATION  → hours, fractional to the half hour (e.g. "1.5")
 *
 * Writes go through the existing PUT /api/v1/sales-orders/{id} endpoint
 * using its udf merge semantics (CLAUDE.md sales-order UDF write rules).
 * "Unassign" writes empty strings to those keys; the dispatch logic treats
 * empty values as "not on the board" so the order moves back to the queue.
 *
 * Date semantics: the board renders a single calendar day at a time. The
 * route filters `placed` to assignments whose `date === selectedDate` —
 * everything else is invisible on the current view (but still persisted on
 * its own day). Undated legacy assignments (FOP_DATE absent or malformed)
 * return null from readAssignment so they fall back to the queue and have
 * to be re-dropped on an explicit day. That's intentional — it avoids
 * surfacing stale single-day assignments as "today" forever.
 *
 * Techs are sourced from the Tinsmith `Tech` table (Prisma-backed) via the
 * admin API — Spire doesn't model technicians as a writable REST resource
 * in 3.13.8, so the roster lives on our side. The `useTechs()` hook below
 * pulls from `/api/v1/admin/techs?active=true`; admin edits surface on the
 * dispatch board within the 30s staleTime.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import { useQuery } from '@tanstack/react-query';
import { api, type Tech } from './api.js';

/** 'all' or a sales-type key (Admin → Sales types). */
export type DispatchTradeFilter = 'all' | string;

/**
 * Tech roster type used by the dispatch board. Mirrors the API `Tech` DTO —
 * kept as a separate alias so callers can import from one place without
 * caring whether the source is server-side or local.
 */
export type Technician = Tech;

/**
 * Fetch the active dispatch roster. Stale-while-revalidate at 30s so
 * roster edits in the admin panel show up on the dispatch board without
 * a hard refresh.
 */
export function useTechs() {
  return useQuery({
    queryKey: ['admin', 'techs', { active: true }] as const,
    queryFn: ({ signal }) => api.admin.techs.list(false, signal),
    staleTime: 30_000,
  });
}

/**
 * Id → name lookup for every tech, inactive included. Unlike `useTechs()`
 * this is readable by field techs, whose screens otherwise fell back to the
 * raw tech id when naming a lead or helper (TFS feedback, 2026-10-08).
 */
export function useTechNames() {
  const query = useQuery({
    queryKey: ['techs', 'names'] as const,
    queryFn: ({ signal }) => api.admin.techs.names(signal),
    staleTime: 5 * 60_000,
  });
  const byId = new Map((query.data ?? []).map((t) => [t.id, t.name]));
  return (id: string): string => byId.get(id) ?? (query.isPending ? '…' : 'Unknown tech');
}

/**
 * Legacy storage anchor. Until 2026-09-22 `fop_start` held an integer slot
 * offset from 8 AM (slot 2 = 10 AM) and `fop_duration` whole hours. It now
 * holds a CLOCK TIME ("10:30") and `fop_duration` fractional hours ("1.5").
 * `readAssignment` still accepts the old form so orders written before the
 * switch — and by any stale PWA bundle — keep loading; every WRITE uses the
 * new form. Do not remove this until no environment has legacy values.
 */
export const LEGACY_SLOT_ANCHOR_HOUR = 8;

/** Smallest schedulable unit, in hours. Every start/duration snaps to it. */
export const BLOCK_HOURS = 0.5;

/**
 * Legacy exports — kept so unrelated callers continue to compile. New code
 * reads the *view* window from `useDispatchBoard` instead.
 */
export const BOARD_HOURS = 8;

/** Default view window — first hour shown + how many hour columns wide. */
export const DEFAULT_VIEW_START_HOUR = 8;
export const DEFAULT_VIEW_HOURS = 8;

/** Bounds for the view window. Must accept anything Admin → Settings allows
 * (day of 4–16 hours starting 0–22), or a saved company setting would be
 * silently clamped on the board. */
export const MIN_VIEW_HOURS = 4;
export const MAX_VIEW_HOURS = 16;
export const MIN_VIEW_START_HOUR = 0;
export const MAX_VIEW_START_HOUR = 22;

/** UDF keys we own on the order header. fop-prefixed so they don't collide
 * with customer-specific keys like "SALES TYP". Lowercase because Spire
 * force-lowercases defined UDF names and keys are case-sensitive; the API
 * bridges legacy FOP_* values on read + write (@tinsmith/shared fop-udf).
 * Keep in sync with FOP_UDF there. */
export const FOP_UDF = {
  tech: 'fop_tech',
  date: 'fop_date',
  start: 'fop_start',
  duration: 'fop_duration',
  /** ISO 8601 timestamp the tech clocked out and marked the visit complete.
   * Empty = still in progress. We don't move the assignment off the board
   * — completed jobs stay placed for the day so the dispatcher can audit
   * the schedule — but cards render a "Done" treatment. */
  completedAt: 'fop_completed_at',
  /** Current field-time-tracking phase. Mirrored from the open TimeEntry
   * on every phase change (apps/api/src/modules/field/field.service.ts).
   * Drives the dispatch-board card colour + day-card pill so office and
   * field see the same status without a Tinsmith API round-trip. */
  phase: 'fop_phase',
  /** Comma-delimited list of additional tech ids assigned as helpers on
   * this job (e.g. ",tech-2,tech-3,"). The lead is whoever's in FOP_TECH;
   * helpers see the job in their My Day but don't drive FOP_PHASE or
   * FOP_COMPLETED_AT — they log their own TimeEntries alongside the lead.
   * Comma boundaries on both ends make Spire's `$contains` filter
   * unambiguous when matching `,techId,`. Empty = no helpers. */
  helpers: 'fop_helpers',
} as const;

/** YYYY-MM-DD only — no time-of-day component, no tz. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface Assignment {
  techId: string;
  /** YYYY-MM-DD (local calendar date) the order is scheduled for. */
  date: string;
  /** Absolute clock hour, fractional to the half hour (10.5 = 10:30 AM). */
  startHour: number;
  /** Duration in hours, clamped to fit within the timeline. */
  duration: number;
}

/**
 * Pull an assignment out of an order's UDF object. Returns null when the
 * FOP keys are absent, empty, or fail to parse — those orders belong in
 * the unassigned queue. Requires FOP_DATE in strict YYYY-MM-DD form;
 * legacy undated assignments deliberately drop back to the queue rather
 * than implicitly claiming "today".
 *
 * `startHour` is an ABSOLUTE clock hour, fractional to the half hour
 * (10.5 = 10:30 AM). Two storage forms are accepted:
 *   - "HH:MM"          — current (2026-09-22+)
 *   - integer slot     — legacy offset from LEGACY_SLOT_ANCHOR_HOUR (8 AM)
 * `duration` is hours, fractional, snapped to BLOCK_HOURS.
 */
export function readAssignment(udf: Record<string, string>): Assignment | null {
  const techId = udf[FOP_UDF.tech]?.trim();
  if (!techId) return null;
  const date = udf[FOP_UDF.date]?.trim();
  if (!date || !ISO_DATE.test(date)) return null;
  const startHour = parseStoredStart(udf[FOP_UDF.start]);
  const durationRaw = udf[FOP_UDF.duration]?.trim();
  const duration = durationRaw ? Number(durationRaw) : NaN;
  if (startHour === null || !Number.isFinite(duration) || duration <= 0) return null;
  return {
    techId,
    date,
    startHour,
    duration: Math.max(BLOCK_HOURS, Math.min(snapToBlock(duration), 24)),
  };
}

const CLOCK_TIME = /^(\d{1,2}):(\d{2})$/;

/**
 * "10:30" → 10.5; a bare integer is a LEGACY slot → 8 + slot. Null when
 * unparseable or outside a plausible day (-4 .. 28 covers overnight work
 * without accepting garbage).
 */
export function parseStoredStart(raw: string | undefined): number | null {
  const v = raw?.trim();
  if (!v) return null;
  const m = CLOCK_TIME.exec(v);
  let hour: number;
  if (m) {
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h > 47 || min > 59) return null;
    hour = h + min / 60;
  } else if (/^-?\d+$/.test(v)) {
    const slot = Number(v);
    if (slot < -12 || slot > 18) return null;
    hour = LEGACY_SLOT_ANCHOR_HOUR + slot;
  } else {
    return null;
  }
  if (hour < -4 || hour > 28) return null;
  return snapToBlock(hour);
}

/** 10.5 → "10:30". What we WRITE to fop_start. */
export function formatStoredStart(hour: number): string {
  const snapped = snapToBlock(hour);
  const h = Math.floor(snapped);
  const m = Math.round((snapped - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function snapToBlock(hours: number): number {
  return Math.round(hours / BLOCK_HOURS) * BLOCK_HOURS;
}

/** Convert an absolute start hour into the visible column index (in BLOCK_HOURS
 * units) for the current view window. Negative or >= column count means the
 * block falls outside the view — the renderer hides those cards. */
export function slotToViewColumn(startHour: number, viewStartHour: number): number {
  return Math.round((startHour - viewStartHour) / BLOCK_HOURS);
}

/** Inverse of `slotToViewColumn` — the absolute start hour for a drop on the
 * given visible column. */
export function viewColumnToSlot(col: number, viewStartHour: number): number {
  return snapToBlock(viewStartHour + col * BLOCK_HOURS);
}

/** Number of BLOCK_HOURS columns a `hours`-long block spans. */
export function hoursToColumns(hours: number): number {
  return Math.max(1, Math.round(hours / BLOCK_HOURS));
}

/** True when the saved start falls within the dispatcher's current view. */
export function isSlotVisible(startHour: number, viewStartHour: number, viewHours: number): boolean {
  const c = slotToViewColumn(startHour, viewStartHour);
  return c >= 0 && c < hoursToColumns(viewHours);
}

/** Build the UDF patch body for assigning an order. Also clears any prior
 * FOP_COMPLETED_AT — re-assigning a previously-finished job (e.g., follow-up
 * visit) starts fresh as "in progress" rather than carrying the old done
 * stamp forward.
 *
 * `resetPhase` should be true ONLY for a fresh placement (order currently
 * unassigned): it wipes any leftover FOP_PHASE (e.g. `paused` from an
 * abandoned earlier visit) so the new booking doesn't render phantom
 * phase chrome. Moves/resizes of an in-flight job must NOT reset — the
 * tech may be actively working it. */
export function assignmentUdfPatch(
  a: Assignment,
  opts: { resetPhase?: boolean } = {},
): Record<string, string> {
  return {
    [FOP_UDF.tech]: a.techId,
    [FOP_UDF.date]: a.date,
    [FOP_UDF.start]: formatStoredStart(a.startHour),
    [FOP_UDF.duration]: formatStoredDuration(a.duration),
    [FOP_UDF.completedAt]: '',
    ...(opts.resetPhase ? { [FOP_UDF.phase]: '' } : {}),
  };
}

/**
 * Resize-in-place patch: start/duration ONLY. FOP_TECH is deliberately
 * omitted (Spire merges UDF keys, so the existing tech survives) — the
 * server logs `dispatch.assign` whenever a patch carries FOP_TECH, and
 * including it here wrote a spurious "Assigned…" activity row for every
 * stepper nudge (audit finding).
 */
export function resizeUdfPatch(startHour: number, duration: number): Record<string, string> {
  return {
    [FOP_UDF.start]: formatStoredStart(startHour),
    [FOP_UDF.duration]: formatStoredDuration(duration),
  };
}

/** 1.5 → "1.5", 2 → "2". What we WRITE to fop_duration. */
export function formatStoredDuration(hours: number): string {
  return String(snapToBlock(hours));
}

/**
 * Cross-day move patch: FOP_DATE only, plus clearing FOP_COMPLETED_AT so a
 * re-scheduled finished visit restarts fresh. Tech/start/duration survive
 * via UDF merge; FOP_TECH omitted for the same no-spurious-log reason.
 */
export function moveDateUdfPatch(date: string): Record<string, string> {
  return {
    [FOP_UDF.date]: date,
    [FOP_UDF.completedAt]: '',
  };
}

/** UDF patch that clears the assignment. Spire keeps the keys but with
 * empty values; the read side treats empty as unassigned. Clearing the
 * tech also clears the completed flag, helper list, AND phase — an
 * unassigned order must not keep counting as "in progress" on Today or
 * carry a paused badge back into the queue. */
export const unassignUdfPatch: Record<string, string> = {
  [FOP_UDF.tech]: '',
  [FOP_UDF.date]: '',
  [FOP_UDF.start]: '',
  [FOP_UDF.duration]: '',
  [FOP_UDF.completedAt]: '',
  [FOP_UDF.helpers]: '',
  [FOP_UDF.phase]: '',
};

/**
 * Parse the helpers list from the order's UDF. Returns a deduped list of
 * tech ids — leading/trailing commas are storage artifacts (delimiter
 * boundaries that make `$contains` filters unambiguous on the server) and
 * are stripped here. If the lead is mistakenly in the helpers list, it's
 * filtered out so callers can treat lead + helpers as disjoint sets.
 */
export function readHelpers(udf: Record<string, string>): string[] {
  const raw = udf[FOP_UDF.helpers]?.trim();
  if (!raw) return [];
  const leadId = udf[FOP_UDF.tech]?.trim() ?? null;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const id = part.trim();
    if (!id) continue;
    if (id === leadId) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Build the UDF patch for setting the helpers list. Wraps the ids in
 * leading/trailing commas so a server-side `$contains: ",techId,"` filter
 * matches without false-positive substring collisions (e.g. matching
 * `tech-1` shouldn't also match `tech-12`). Empty array writes ""
 * (clears the key).
 */
export function helpersUdfPatch(helperIds: ReadonlyArray<string>): Record<string, string> {
  const cleaned = Array.from(
    new Set(helperIds.map((id) => id.trim()).filter((id) => id.length > 0)),
  );
  if (cleaned.length === 0) return { [FOP_UDF.helpers]: '' };
  return { [FOP_UDF.helpers]: `,${cleaned.join(',')},` };
}

/** Parse the completion timestamp from an order's UDF. Returns null if the
 * key is absent, empty, or unparseable. Tolerant of any ISO 8601 string —
 * we round-trip whatever the client wrote. */
export function readCompletedAt(udf: Record<string, string>): Date | null {
  const raw = udf[FOP_UDF.completedAt]?.trim();
  if (!raw) return null;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? new Date(t) : null;
}

/** UDF patch a field tech writes when clocking out of a visit. Pair with
 * `salesOrders.update`. We don't touch tech/date/start/duration here so
 * the schedule history stays intact on the dispatcher's board. */
export function completedUdfPatch(at: Date = new Date()): Record<string, string> {
  return { [FOP_UDF.completedAt]: at.toISOString() };
}

/**
 * Today's date as YYYY-MM-DD in the browser's local timezone. We avoid
 * toISOString() because that returns UTC, which rolls a day for users
 * near midnight — a dispatcher in Vancouver assigning at 11:30 PM would
 * see "tomorrow" on the board.
 */
export function todayLocalIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Shift a YYYY-MM-DD by N days (positive or negative). Locale-safe. */
export function shiftIsoDate(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export interface DispatchActivity {
  id: string;
  /** Tone of the activity icon — matches the .ic-a..e classes in the mockup. */
  tone: 'success' | 'info' | 'warning' | 'danger' | 'install';
  text: string;
  at: string;
}

/**
 * Local-only UI state — selection, filter, selected-day, activity feed.
 * Assignments themselves now live in Spire and arrive via the orders query.
 *
 * `selectedDate` defaults to today and is NOT persisted across reloads —
 * dispatchers reopen the board most days to plan today, and "page state
 * matches my mental model" beats "remembering Friday's selection on Monday".
 */
interface DispatchState {
  filter: DispatchTradeFilter;
  selectedOrderId: number | null;
  selectedDate: string;
  /** First hour (0..23) shown on the board. Persists to localStorage so
   * a shop running 7a–7p doesn't re-pick this every page load. */
  viewStartHour: number;
  /** Number of hour columns shown. Persists alongside viewStartHour. */
  viewHours: number;
  activity: DispatchActivity[];

  select(orderId: number | null): void;
  setFilter(filter: DispatchTradeFilter): void;
  setSelectedDate(date: string): void;
  shiftSelectedDate(days: number): void;
  goToToday(): void;
  setViewWindow(startHour: number, hours: number): void;
  pushActivity(entry: Omit<DispatchActivity, 'id' | 'at'>): void;
  clearActivity(): void;
}

const VIEW_STORAGE_KEY = 'tinsmith.dispatch-view-window';

/** True when this browser has a dispatcher-chosen view window saved. */
function hasSavedViewWindow(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage?.getItem(VIEW_STORAGE_KEY);
  } catch {
    return false;
  }
}

/**
 * Apply the company's day window (Admin → Settings) as the board's view
 * unless the dispatcher has saved their own via the WorkdayPicker. Runs
 * again whenever the settings change, so an admin edit shows up on every
 * board that hasn't been customised.
 */
export function useApplyCompanyDayWindow(settings: { dayStartHour: number; dayEndHour: number }): void {
  const setViewWindow = useDispatchBoard((s) => s.setViewWindow);
  useEffect(() => {
    if (hasSavedViewWindow()) return;
    const hours = settings.dayEndHour - settings.dayStartHour;
    useDispatchBoard.setState({
      viewStartHour: clampInt(settings.dayStartHour, MIN_VIEW_START_HOUR, MAX_VIEW_START_HOUR, DEFAULT_VIEW_START_HOUR),
      viewHours: clampInt(hours, MIN_VIEW_HOURS, MAX_VIEW_HOURS, DEFAULT_VIEW_HOURS),
    });
    // setViewWindow deliberately NOT used: it would persist the company
    // default as if the dispatcher had chosen it.
  }, [settings.dayStartHour, settings.dayEndHour, setViewWindow]);
}

function loadViewWindow(): { startHour: number; hours: number } {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { startHour: DEFAULT_VIEW_START_HOUR, hours: DEFAULT_VIEW_HOURS };
  }
  try {
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return { startHour: DEFAULT_VIEW_START_HOUR, hours: DEFAULT_VIEW_HOURS };
    const parsed = JSON.parse(raw) as { startHour?: unknown; hours?: unknown };
    const startHour = Number(parsed.startHour);
    const hours = Number(parsed.hours);
    return {
      startHour: clampInt(startHour, MIN_VIEW_START_HOUR, MAX_VIEW_START_HOUR, DEFAULT_VIEW_START_HOUR),
      hours: clampInt(hours, MIN_VIEW_HOURS, MAX_VIEW_HOURS, DEFAULT_VIEW_HOURS),
    };
  } catch {
    return { startHour: DEFAULT_VIEW_START_HOUR, hours: DEFAULT_VIEW_HOURS };
  }
}

function saveViewWindow(startHour: number, hours: number): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ startHour, hours }));
  } catch {
    // Quota / private-mode — silently ignore.
  }
}

function clampInt(value: number, lo: number, hi: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  const i = Math.floor(value);
  if (i < lo) return lo;
  if (i > hi) return hi;
  return i;
}

function nowLabel(): string {
  const d = new Date();
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const am = h >= 12 ? 'PM' : 'AM';
  h = ((h + 11) % 12) + 1;
  return `${h}:${m} ${am}`;
}

function activityId(): string {
  return `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export const useDispatchBoard = create<DispatchState>()((set, get) => {
  const initialView = loadViewWindow();
  return {
  filter: 'all',
  selectedOrderId: null,
  selectedDate: todayLocalIso(),
  viewStartHour: initialView.startHour,
  viewHours: initialView.hours,
  activity: [],

  select(orderId) {
    set({ selectedOrderId: orderId });
  },
  setFilter(filter) {
    set({ filter });
  },
  setSelectedDate(date) {
    if (!ISO_DATE.test(date)) return;
    set({ selectedDate: date });
  },
  shiftSelectedDate(days) {
    set({ selectedDate: shiftIsoDate(get().selectedDate, days) });
  },
  goToToday() {
    set({ selectedDate: todayLocalIso() });
  },
  setViewWindow(startHour, hours) {
    const s = clampInt(startHour, MIN_VIEW_START_HOUR, MAX_VIEW_START_HOUR, DEFAULT_VIEW_START_HOUR);
    const h = clampInt(hours, MIN_VIEW_HOURS, MAX_VIEW_HOURS, DEFAULT_VIEW_HOURS);
    saveViewWindow(s, h);
    set({ viewStartHour: s, viewHours: h });
  },
  pushActivity(entry) {
    const next: DispatchActivity = { ...entry, id: activityId(), at: nowLabel() };
    const activity = [next, ...get().activity].slice(0, 20);
    set({ activity });
  },
  clearActivity() {
    set({ activity: [] });
  },
  };
});

/** Label for an absolute (possibly half) hour: 10.5 → "10:30 AM". */
export function hourLabel(hour: number): string {
  return absoluteHourLabel(hour);
}

export function absoluteHourLabel(h: number): string {
  const total = ((h % 24) + 24) % 24;
  const hh = Math.floor(total);
  const mm = Math.round((total - hh) * 60);
  const suffix = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return mm === 0 ? `${h12} ${suffix}` : `${h12}:${String(mm).padStart(2, '0')} ${suffix}`;
}

/** Compact label for tech screens: 8 → "8a", 10.5 → "10:30a", 13 → "1p". */
export function compactHourLabel(hour: number): string {
  const total = ((hour % 24) + 24) % 24;
  const hh = Math.floor(total);
  const mm = Math.round((total - hh) * 60);
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  const suffix = hh < 12 ? 'a' : 'p';
  return mm === 0 ? `${h12}${suffix}` : `${h12}:${String(mm).padStart(2, '0')}${suffix}`;
}

/** "8a – 12p" for an assignment's window; hours shown to the half hour. */
export function windowLabel(startHour: number, duration: number): string {
  return `${compactHourLabel(startHour)} – ${compactHourLabel(startHour + duration)}`;
}

/** "1.5h" / "2h" — trims the ".0". */
export function durationLabel(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}
